import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import FormField from '../components/FormField'
import GradientButton from '../components/GradientButton'
import { useT } from '../i18n'
import type { TranslationKey } from '../i18n/en'
import { ApiError, api, getApiErrorCode, type TeamMemberCreateResponse } from '../utils/api'
import {
  TEAM_MEMBER_PASSWORD_MIN_LENGTH,
  extractValidationIssues,
  mapServerIssuesToFieldErrors,
  validateTeamMemberDraft,
  type TeamMemberFieldError,
  type TeamMemberFormField,
  type TeamMemberMessages
} from '../utils/teamMemberValidation'

// Findings #117 (rehearsal 1): "Nuevo miembro" asks the member type first and
// shows only that type's fields, in Spanish.
// - Operador: a person who records in Crew Mode with a PIN on a shared phone;
//   no web sign-in (role viewer is enough: the PIN pad lists every member with
//   a PIN and records are attributed to the operator who typed it).
// - Persona con acceso web: office / supervisors; email + username + password.
// - Celular del equipo: one device account per crew phone (contributor — the
//   lowest role that can save records); the phone signs in once with it.

type MemberKind = 'operator' | 'person' | 'device'
type PersonRole = 'viewer' | 'contributor' | 'editor'

type FieldErrorMap = Partial<Record<TeamMemberFormField, string>>

const collapseErrors = (errors: ReadonlyArray<TeamMemberFieldError>): FieldErrorMap => {
  const out: FieldErrorMap = {}
  for (const entry of errors) {
    // First message per field wins so the user is not whiplashed between
    // overlapping issues (e.g. format + length on the same field).
    out[entry.field] ??= entry.message
  }
  return out
}

const KIND_LABEL: Record<MemberKind, TranslationKey> = {
  operator: 'createMember.kind.operator',
  person: 'createMember.kind.person',
  device: 'createMember.kind.device'
}
const KIND_HELP: Record<MemberKind, TranslationKey> = {
  operator: 'createMember.kind.operatorHelp',
  person: 'createMember.kind.personHelp',
  device: 'createMember.kind.deviceHelp'
}
const ROLE_LABEL: Record<PersonRole, TranslationKey> = {
  viewer: 'createMember.role.viewer',
  contributor: 'createMember.role.contributor',
  editor: 'createMember.role.editor'
}

const boxStyle: React.CSSProperties = { padding: 12, borderRadius: 12, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1e3a8a' }

export default function CreateAccount() {
  const t = useT()
  const navigate = useNavigate()
  const [kind, setKind] = useState<MemberKind | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')
  const [role, setRole] = useState<PersonRole>('editor')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({})
  const [created, setCreated] = useState<{ kind: MemberKind; result: TeamMemberCreateResponse; pinSet: boolean } | null>(null)

  const messages: TeamMemberMessages = {
    displayNameRequired: t('createMember.error.name'),
    emailInvalid: t('createMember.error.email'),
    usernameLength: t('createMember.error.usernameLength'),
    usernameFormat: t('createMember.error.usernameFormat'),
    passwordTooShort: t('createMember.error.password', { min: TEAM_MEMBER_PASSWORD_MIN_LENGTH }),
    credentialsTrio: t('createMember.error.credentials')
  }

  const reset = () => {
    setName('')
    setEmail('')
    setUsername('')
    setPassword('')
    setPin('')
    setFieldErrors({})
    setError('')
  }

  const chooseKind = (next: MemberKind) => {
    reset()
    setKind(next)
    setRole(next === 'device' ? 'contributor' : next === 'operator' ? 'viewer' : 'editor')
    if (next === 'device') setName(t('createMember.devicePlaceholderName'))
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!kind) return
    const withSignIn = kind !== 'operator'
    if (withSignIn && (!email.trim() || !username.trim() || !password)) {
      setFieldErrors({ credentialsTrio: messages.credentialsTrio })
      return
    }
    if (kind === 'operator' && pin.trim() && !/^\d{4}$/.test(pin.trim())) {
      setError(t('ownerConsole.pin.invalid'))
      return
    }
    const issues = validateTeamMemberDraft({
      displayName: name,
      email: withSignIn ? email : '',
      username: withSignIn ? username : '',
      password: withSignIn ? password : ''
    }, messages)
    if (issues.length > 0) {
      setFieldErrors(collapseErrors(issues))
      setError('')
      return
    }

    setLoading(true)
    setError('')
    setFieldErrors({})
    try {
      const result = await api.teamMembers.create({
        displayName: name.trim(),
        role: kind === 'device' ? 'contributor' : kind === 'operator' ? 'viewer' : role,
        ...(withSignIn ? { email: email.trim(), username: username.trim(), password } : {})
      })
      let pinSet = false
      if (kind === 'operator' && pin.trim()) {
        await api.teamMembers.setPin(result.member.id, pin.trim())
        pinSet = true
      }
      setCreated({ kind, result, pinSet })
      reset()
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const serverIssues = extractValidationIssues(err.body)
        if (serverIssues.length > 0) {
          setFieldErrors(collapseErrors(mapServerIssuesToFieldErrors(serverIssues)))
          setError('')
          return
        }
      }
      // Findings #168: email / username already used by another account.
      const code = getApiErrorCode(err)
      if (code === 'emailTaken' || code === 'usernameTaken') {
        setFieldErrors(code === 'emailTaken'
          ? { email: t('createMember.error.emailTaken') }
          : { username: t('createMember.error.usernameTaken') })
        setError('')
        return
      }
      setError(err instanceof Error ? err.message : t('createMember.error.generic'))
    } finally {
      setLoading(false)
    }
  }

  if (created) {
    const member = created.result.member
    return (
      <div style={{ maxWidth: 640 }}>
        <div className="form-card" style={{ display: 'grid', gap: 10 }} data-testid="created-member-card">
          <strong>{t('createMember.created.title', { name: member.label })}</strong>
          {created.kind === 'operator' ? (
            <div role="status" style={boxStyle} data-testid="created-member-next-operator">
              {created.pinSet ? t('createMember.created.operatorWithPin') : t('createMember.created.operatorNoPin')}
            </div>
          ) : created.kind === 'device' ? (
            <div role="status" style={boxStyle} data-testid="created-member-next-device">
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('createMember.created.deviceTitle')}</div>
              <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4 }}>
                <li>{t('createMember.created.deviceStep1')}</li>
                <li>{t('createMember.created.deviceStep2', { username: member.username ?? '' })}</li>
                <li>{t('createMember.created.deviceStep3')}</li>
                <li>{t('createMember.created.deviceStepIphone')}</li>
              </ol>
            </div>
          ) : (
            <div role="status" style={{ ...boxStyle, borderColor: '#fde68a', background: '#fffbeb', color: '#92400e' }} data-testid="created-member-credentials-reminder">
              {t('createMember.created.personNext', { username: member.username ?? '' })}
            </div>
          )}
          {member.username ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{t('createMember.created.username', { username: member.username })}</span>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(member.username ?? '')}
                data-testid="created-member-copy-username"
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontWeight: 600, cursor: 'pointer' }}
              >
                {t('createMember.created.copyUsername')}
              </button>
            </div>
          ) : null}
          <details>
            <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>{t('createMember.technicalDetails')}</summary>
            <div style={{ fontSize: 12, color: '#64748b', display: 'grid', gap: 2, marginTop: 6 }}>
              <span data-testid="created-member-actor-id">Actor ID: {member.actorId}</span>
              <span>User ID: {member.userId}</span>
              <span>Membership ID: {member.tenantMembershipId}</span>
            </div>
          </details>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <GradientButton size="sm" variant="secondary" onClick={() => { setCreated(null); setKind(null) }}>{t('createMember.created.another')}</GradientButton>
            <GradientButton size="sm" variant="secondary" onClick={() => navigate('/owner')}>{t('createMember.created.back')}</GradientButton>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 className="h2">{t('createMember.title')}</h2>
      <p className="lead">{t('createMember.lead')}</p>

      <div role="radiogroup" aria-label={t('createMember.kindQuestion')} style={{ display: 'grid', gap: 8, marginTop: 12 }}>
        <strong>{t('createMember.kindQuestion')}</strong>
        {(['operator', 'person', 'device'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={kind === option}
            onClick={() => chooseKind(option)}
            data-testid={`create-member-kind-${option}`}
            style={{
              textAlign: 'left', padding: 12, borderRadius: 12, cursor: 'pointer',
              border: kind === option ? '2px solid #1d4ed8' : '1px solid #cbd5e1',
              background: kind === option ? '#eff6ff' : '#ffffff'
            }}
          >
            <div style={{ fontWeight: 700 }}>{t(KIND_LABEL[option])}</div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>{t(KIND_HELP[option])}</div>
          </button>
        ))}
      </div>

      {kind ? (
        <div className="form-card" style={{ marginTop: 12 }}>
          <form onSubmit={onSubmit} className="form-grid">
            <FormField id="ca-name" label={t(kind === 'device' ? 'createMember.field.deviceName' : 'createMember.field.name')} value={name} onChange={(value) => setName(String(value))} error={fieldErrors.displayName} />

            {kind === 'operator' ? (
              <FormField id="ca-pin" label={t('createMember.field.pin')} value={pin} placeholder="1234" onChange={(value) => setPin(String(value).replace(/\D/g, '').slice(0, 4))} />
            ) : (
              <>
                <FormField id="ca-email" label={t('createMember.field.email')} type="email" value={email} placeholder={t(kind === 'device' ? 'createMember.field.deviceEmailPlaceholder' : 'createMember.field.emailPlaceholder')} onChange={(value) => setEmail(String(value))} error={fieldErrors.email} />
                <FormField id="ca-username" label={t('createMember.field.username')} value={username} placeholder={kind === 'device' ? 'celular1' : 'jperez'} onChange={(value) => setUsername(String(value))} error={fieldErrors.username} />
                <FormField id="ca-password" label={t('createMember.field.password')} type="password" value={password} placeholder={t('createMember.field.passwordPlaceholder', { min: TEAM_MEMBER_PASSWORD_MIN_LENGTH })} onChange={(value) => setPassword(String(value))} error={fieldErrors.password} />
              </>
            )}

            {kind === 'person' ? (
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>{t('createMember.field.role')}</span>
                <select value={role} onChange={(e) => setRole(e.target.value as PersonRole)} style={{ padding: 12, borderRadius: 12, border: '1px solid rgba(148,163,184,.35)', background: 'white' }}>
                  {(['viewer', 'contributor', 'editor'] as const).map((option) => (
                    <option key={option} value={option}>{t(ROLE_LABEL[option])}</option>
                  ))}
                </select>
              </label>
            ) : null}

            <div style={boxStyle}>{t(kind === 'operator' ? 'createMember.note.operator' : kind === 'device' ? 'createMember.note.device' : 'createMember.note.person')}</div>

            {fieldErrors.credentialsTrio ? (
              <div role="alert" data-testid="create-account-trio-error" style={{ padding: 12, borderRadius: 12, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b' }}>
                {fieldErrors.credentialsTrio}
              </div>
            ) : null}

            {error ? (
              <div role="alert" style={{ padding: 12, borderRadius: 12, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b' }}>
                {error}
              </div>
            ) : null}

            <div>
              <GradientButton type="submit" fullWidth disabled={loading || !name.trim()} data-testid="create-member-submit">
                {loading ? t('createMember.creating') : t('createMember.submit')}
              </GradientButton>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}
